/**
 * ProviderOrg memberships — derived from ``GET /auth/me``.
 *
 * The BE returns the membership list inline on ``/me`` so the admin
 * shell needs only one fetch on mount. This hook reads the same cache
 * entry without a second network call. ``isLoading`` mirrors the
 * underlying ``useAuthMe`` query so consumers can render a skeleton
 * while auth is still resolving.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { useAuthMe } from "@/api/queries/auth";
import type {
  CustomerOrgPublicCreate,
  CustomerOrgPublicCreateResponse,
  OrgDomainStatus,
  OrgMembershipOut,
  StripeModeResponse,
} from "@/api/types";
import { apiFetch } from "@/lib/api";
import {
  PLATFORM_APEX_DOMAINS,
  TENANT_APEX_DOMAINS,
  isIpLiteral,
  normalizeHostname,
} from "@/shell/tenantHosts";

import { adminKeys, queryKeys } from "./keys";

const API = "/api/v1";

interface UseMyOrganizationsResult {
  organizations: OrgMembershipOut[];
  activeOrgSlug: string | null;
  isLoading: boolean;
  isError: boolean;
}

export const ACTIVE_ORG_STORAGE_KEY = "forgmatic.active_org_slug";

function readStoredActiveOrgSlug(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Tenant slug implied by the current hostname.
 *
 * Tenant subdomains follow ``<slug>.forgmatic.com`` (or the legacy
 * ``.cz`` apex during the cutover). When the user is browsing
 * ``demo.forgmatic.com`` the OrgSwitcher should treat ``demo`` as
 * the active org regardless of what localStorage says — otherwise
 * the displayed active drifts from reality, and the
 * "is the click a no-op?" guard inside ``OrgSwitcher.onPick`` fires
 * for the wrong slug (user clicks ``Default Organization`` thinking
 * they'll switch there, but the guard says it's already active and
 * nothing happens).
 *
 * Returns ``null`` for the apex (``forgmatic.com``), the platform-
 * admin shell (``admin.``), the marketing prefix (``www.``) and
 * hosts under no configured apex (dev / Vercel previews) — those
 * callers fall through to the localStorage hint and ultimately to
 * ``orgs[0]``.
 *
 * The apex list lives in ``@/shell/tenantHosts`` — shared with the URL
 * *builders*, which took a hostname apart under a different (hardcoded)
 * idea of what the apex is and so navigated dev servers to production.
 * It was a hardcoded ``forgmatic.(com|cz)`` regex here too, which made
 * every caller DEAD outside those two apexes — including
 * ``AdminLayout``'s setup-incomplete guard, whose whole job is to send a
 * freshly signed-up owner into the onboarding wizard. Under e2e (and any
 * non-Forgmatic deployment) that guard silently never fired, so a
 * brand-new tenant landed in an empty admin instead.
 */
const SYSTEM_SUBDOMAINS: ReadonlySet<string> = new Set([
  "admin", "blog", "docs", "help", "login", "mail", "signup", "sso", "status", "www",
]);

/** Exported for the unit test — hostname in, slug out, no ``window``. */
export function tenantSlugFromHostname(hostname: string): string | null {
  const host = normalizeHostname(hostname);
  const apex = TENANT_APEX_DOMAINS.find((d) => host.endsWith(`.${d}`));
  if (!apex) return null;
  const slug = host.slice(0, -(apex.length + 1));
  // Single label only — ``a.b.forgmatic.com`` is not a tenant host,
  // and the character class keeps the slug DNS-shaped.
  if (!/^[a-z0-9-]+$/.test(slug)) return null;
  if (SYSTEM_SUBDOMAINS.has(slug)) return null;
  return slug;
}

export function readActiveOrgSlugFromHost(): string | null {
  if (typeof window === "undefined" || !window.location) return null;
  return tenantSlugFromHostname(window.location.hostname);
}

/**
 * Host VYPADÁ jako tenantní (``<slug>.<něco>``), ale jeho apex tenhle
 * build nezná — tedy ani neumí říct, kterého tenanta si zákazník
 * prohlíží.
 *
 * Vzniká to rozejitím dvou nezávislých konfigurací: API si tenanta
 * odvozuje z ``TENANT_DOMAIN`` (Render), frontend ze seznamu
 * ``VITE_TENANT_APEX_DOMAINS`` (build Vercelu). Když jedna strana
 * ukazuje na ``forgmatic.app`` a druhá na ``forgmatic.com``, request
 * doputuje k tenantovi z hostu, ale UI o něm neví — a dosud si beze
 * slova domyslelo prvního tenanta uživatele. To je KAN-11: adresa
 * ``superduper.…``, přepínač „demo".
 *
 * Vlastní doména tenanta (``acme.cz``, dvě návěští) sem NESPADÁ:
 * tam frontend slug nikdy neodvozoval a fallback na uloženou / první
 * organizaci je pro ni pořád to nejlepší, co má.
 */
export function isUnknownTenantApexHost(hostname: string): boolean {
  const host = normalizeHostname(hostname);
  if (!host) return false;
  // IP adresa není ``<slug>.<apex>`` — tečky v ní oddělují oktety.
  // Bez tohohle řádku prošla ``127.0.0.1`` jako tenant „127" na
  // neznámém apexu a celý admin na ní přišel o aktivní organizaci
  // (KAN-240).
  if (isIpLiteral(host)) return false;
  const known = [...TENANT_APEX_DOMAINS, ...PLATFORM_APEX_DOMAINS];
  if (known.some((d) => host === d || host.endsWith(`.${d}`))) return false;
  const labels = host.split(".");
  if (labels.length < 3) return false;
  const slug = labels[0] ?? "";
  if (!/^[a-z0-9-]+$/.test(slug)) return false;
  return !SYSTEM_SUBDOMAINS.has(slug);
}

/**
 * Per-org admin gate.
 *
 * The global ``User.role`` is the **customer-facing** role (default
 * ``customer`` for fresh signups via ``POST /orgs/signup-verify``).
 * Admin authority over a tenant lives on the per-org
 * ``OrganizationMember.role`` and surfaces in ``me.org_memberships[]``.
 * Gating tenant-admin chrome on the global role therefore locks out
 * brand-new owners (whose global role stays ``customer`` after signup).
 *
 * Returns true when the signed-in user has ``role=admin`` membership in
 * the **currently active** ProviderOrg (slug from hostname / localStorage
 * via :func:`useMyOrganizations`). When the active slug can't be derived
 * (apex host, dev preview) falls back to "admin of any tenant" — good
 * enough for shared chrome like ``AccountMenu``.
 */
export function useIsCurrentOrgAdmin(): {
  isAdmin: boolean;
  isLoading: boolean;
} {
  const me = useAuthMe();
  const { activeOrgSlug, isLoading } = useMyOrganizations();
  const memberships = me.data?.org_memberships ?? [];
  // When the URL points at a tenant subdomain (``<slug>.forgmatic.com``)
  // the admin gate MUST pin to that slug, not to ``activeOrgSlug`` —
  // ``useMyOrganizations`` falls back to the caller's first membership
  // when the host slug doesn't match (so a user without any role on
  // ``awesome`` would otherwise inherit admin from their own org and
  // render the admin shell while the API requests still target
  // ``awesome``). Pinning to the host slug closes that split-brain.
  const slugFromHost = readActiveOrgSlugFromHost();
  const targetSlug = slugFromHost ?? activeOrgSlug;
  const isAdmin = targetSlug
    ? memberships.some(
        (m) => m.slug === targetSlug && m.role === "admin",
      )
    : memberships.some((m) => m.role === "admin");
  return { isAdmin, isLoading: isLoading || me.isLoading };
}

/**
 * Dílna E6 — operátor (role ``production``) aktivního tenanta. Táž
 * pinovací logika jako ``useIsCurrentOrgAdmin`` (host slug vítězí);
 * řídí vstup do terminálu (odkaz v menu + přesměrování z /admin).
 */
export function useIsCurrentOrgOperator(): {
  isOperator: boolean;
  isLoading: boolean;
} {
  const me = useAuthMe();
  const { activeOrgSlug, isLoading } = useMyOrganizations();
  const memberships = me.data?.org_memberships ?? [];
  const slugFromHost = readActiveOrgSlugFromHost();
  const targetSlug = slugFromHost ?? activeOrgSlug;
  const isOperator = targetSlug
    ? memberships.some(
        (m) => m.slug === targetSlug && m.role === "production",
      )
    : memberships.some((m) => m.role === "production");
  return { isOperator, isLoading: isLoading || me.isLoading };
}

export function useMyOrganizations(): UseMyOrganizationsResult {
  const me = useAuthMe();
  const orgs = me.data?.org_memberships ?? [];
  // Active-org resolution prefers the source closest to ground truth:
  //
  //   1. **Hostname** — when the user browses
  //      ``<slug>.forgmatic.com`` the page IS that tenant; anything
  //      else would be a UI lie.
  //   2. **localStorage** — last explicit pick from the switcher,
  //      used on hosts where the slug isn't encoded (apex, dev,
  //      Vercel previews).
  //   3. **First membership** — fallback when neither is available.
  //
  // The chosen slug is hoisted to the front so consumers that read
  // ``organizations[0]`` (e.g. ``OrgSwitcher.active``) see the right
  // entry without extra wiring.
  const fromHost = readActiveOrgSlugFromHost();
  const hostSlugMatchesOrg =
    fromHost !== null && orgs.some((o) => o.slug === fromHost);
  // KAN-11: na hostu s tenantním tvarem, jehož apex tenhle build nezná,
  // radši NEUKAZUJ nic než cizího tenanta. Uložená volba i „první
  // členství" tu obojí lžou — request míří jinam, než co by UI tvrdilo.
  const unknownTenantHost =
    fromHost === null &&
    typeof window !== "undefined" &&
    Boolean(window.location) &&
    isUnknownTenantApexHost(window.location.hostname);
  const preferred = hostSlugMatchesOrg
    ? fromHost
    : unknownTenantHost
      ? null
      : readStoredActiveOrgSlug();
  const sorted =
    preferred && orgs.some((o) => o.slug === preferred)
      ? [
          ...orgs.filter((o) => o.slug === preferred),
          ...orgs.filter((o) => o.slug !== preferred),
        ]
      : orgs;
  const active = unknownTenantHost
    ? null
    : sorted.length > 0
      ? sorted[0]?.slug ?? null
      : null;
  return {
    organizations: sorted,
    activeOrgSlug: active,
    isLoading: me.isLoading,
    isError: me.isError,
  };
}

// --- Membership lifecycle + user ordering (KAN-1357) ---------------

/**
 * Is this membership one the user can actually enter?
 *
 * ``status`` is absent on a payload from an API older than KAN-1357.
 * Treating that as active is the only safe default: the alternative
 * hides every tenant the moment the FE ships ahead of the BE.
 */
export function isOrgActive(org: OrgMembershipOut): boolean {
  return (org.status ?? "active") === "active";
}

/**
 * Sort memberships the way the user arranged them.
 *
 * ``sort_order`` wins where it is set; everything untouched follows
 * alphabetically. That is the only reading under which "the table is
 * alphabetical" and "drag & drop changes the order" are both true: the
 * alphabet is the DEFAULT, and an explicit drag replaces it.
 *
 * The BE already returns rows in this order. Re-doing it here is not
 * belt-and-braces — the page re-sorts optimistically after a drag,
 * before the refetched ``/auth/me`` lands, and the two have to agree or
 * the row visibly jumps back and forth.
 */
export function sortOrgsForDisplay(
  orgs: OrgMembershipOut[],
): OrgMembershipOut[] {
  return [...orgs].sort((a, b) => {
    const ao = a.sort_order ?? null;
    const bo = b.sort_order ?? null;
    if (ao !== null && bo !== null && ao !== bo) return ao - bo;
    // Unordered rows sink below ordered ones rather than floating to the
    // top of a list the user arranged by hand.
    if (ao !== null && bo === null) return -1;
    if (ao === null && bo !== null) return 1;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Persist the user's ordering of their memberships.
 *
 * ``PUT /auth/me/org-order`` is a per-user preference and needs only a
 * session — no tenant admin, no tenant context. On success we drop the
 * auth cache so the switcher (which reads the same ``/auth/me``
 * payload) picks the new order up without its own plumbing.
 */
export function useSetOrgOrder(
  options?: Omit<UseMutationOptions<void, Error, number[]>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, number[]>({
    mutationFn: (orgIds) =>
      apiFetch<void>(`${API}/auth/me/org-order`, {
        method: "PUT",
        body: { org_ids: orgIds },
      }),
    ...options,
    onSuccess: async (data, vars, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      await options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

/**
 * Stripe Connect onboarding (PR-62).
 *
 * POSTs to ``/api/v1/orgs/{slug}/stripe-connect`` and returns the
 * Stripe Express onboarding URL. The component handles redirect via
 * ``window.location`` so the test surface (window.open spy) stays
 * easy to intercept.
 */
interface StripeConnectResponse {
  url: string;
}

export function useStripeConnectOnboarding(
  options?: Omit<
    UseMutationOptions<StripeConnectResponse, Error, string>,
    "mutationFn"
  >,
) {
  return useMutation<StripeConnectResponse, Error, string>({
    mutationFn: (slug) =>
      apiFetch<StripeConnectResponse>(
        `${API}/orgs/${slug}/stripe-connect`,
        { method: "POST" },
      ),
    ...options,
  });
}

/**
 * Stripe Connect disconnect.
 *
 * DELETE /orgs/{slug}/stripe-connect unlinks the org's Connect account
 * for the *active* mode (test vs live) and returns the refreshed mode
 * state. On success we invalidate the auth cache so /auth/me re-fetches
 * the memberships list (which carries ``stripe_account_id`` for the
 * active mode) and the wizard flips back to its "not connected" branch.
 */
/**
 * KAN-1486 — admin tenanta si přejmenuje organizaci (jen zobrazované
 * jméno, slug zůstává). Jméno čte shell z ``/auth/me``, proto invalidace.
 */
export function useRenameOrg() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { slug: string; name: string }>({
    mutationFn: ({ slug, name }) =>
      apiFetch(`${API}/orgs/${slug}/name`, { method: "PATCH", body: { name } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.me() }),
  });
}

export function useStripeConnectDisconnect(
  options?: Omit<
    UseMutationOptions<StripeModeResponse, Error, string>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<StripeModeResponse, Error, string>({
    mutationFn: (slug) =>
      apiFetch<StripeModeResponse>(`${API}/orgs/${slug}/stripe-connect`, {
        method: "DELETE",
      }),
    ...options,
    onSuccess: async (data, vars, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      await options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

/**
 * Stripe test/live mode toggle (zz39).
 *
 * PUT /orgs/{slug}/stripe-mode flips the org between live and test.
 * On success we invalidate the auth cache so /auth/me re-fetches the
 * memberships list (which carries ``stripe_test_mode`` +
 * ``stripe_account_id`` for the active mode).
 */
export interface UpdateStripeModeArgs {
  slug: string;
  mode: "live" | "test";
}

export function useUpdateStripeMode(
  options?: Omit<
    UseMutationOptions<StripeModeResponse, Error, UpdateStripeModeArgs>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<StripeModeResponse, Error, UpdateStripeModeArgs>({
    mutationFn: ({ slug, mode }) =>
      apiFetch<StripeModeResponse>(`${API}/orgs/${slug}/stripe-mode`, {
        method: "PUT",
        body: { mode },
      }),
    ...options,
    onSuccess: async (data, vars, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      await options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

// --- Domain provisioning status -------------------------------------

/** Poll whether a freshly-provisioned tenant hostname serves yet.
 *
 * Polls until ``ready``, then stops. ``staleTime: 0`` and no cache
 * reuse on purpose — this is a liveness question whose answer changes
 * under us; a cached "not ready" would freeze the waiting screen for
 * the whole session.
 *
 * The 3 s interval is paced against certificate issuance (seconds to
 * minutes), not against a spinner. Faster would add load without
 * shortening the wait, because nothing we do here makes Let's Encrypt
 * answer sooner.
 */
export function useOrgDomainStatus(
  slug: string | null,
  options?: Omit<UseQueryOptions<OrgDomainStatus>, "queryKey" | "queryFn">,
) {
  return useQuery<OrgDomainStatus>({
    queryKey: queryKeys.orgDomainStatus(slug ?? ""),
    queryFn: () =>
      apiFetch<OrgDomainStatus>(`${API}/orgs/${slug}/domain-status`),
    enabled: Boolean(slug),
    refetchInterval: (query) => (query.state.data?.ready ? false : 3000),
    staleTime: 0,
    gcTime: 0,
    // A failed poll is "not ready yet", not an error state to render:
    // the API may be mid-deploy while the tenant is provisioning.
    retry: true,
    ...options,
  });
}

// --- CustomerOrg public self-serve create (PR-D) -------------------
//
// Posts to the new BE endpoint added in PR-D. Caller must already
// have a session (the FE drives this from the post-magic-link
// finalize step). On 201 we invalidate the auth cache so the new
// CustomerOrg membership shows up in /auth/me + the AccountMenu
// dropdown immediately.

export function useCustomerOrgPublicCreate(
  options?: Omit<
    UseMutationOptions<
      CustomerOrgPublicCreateResponse,
      Error,
      CustomerOrgPublicCreate
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    CustomerOrgPublicCreateResponse,
    Error,
    CustomerOrgPublicCreate
  >({
    mutationFn: (payload) =>
      apiFetch<CustomerOrgPublicCreateResponse>(
        `${API}/customer-orgs/public-create`,
        { method: "POST", body: payload },
      ),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}
